import { Component, Input, OnInit, OnDestroy } from '@angular/core';

import { TreeNode } from 'primeng/api';
import { Subscription } from 'rxjs';

import { AbstractDialogComponent } from '../../../../Core/Dialogs/components/AbstractDialog/abstract-dialog.component';
import { CoreDialogService } from '../../../../Core/Dialogs/services/dialog.service';
import { DropdownOptionModel } from '../../../../Common/Forms/models/dropdown-option.model';
import { BriefingTemplateService } from '../../../../Common/MonitorShared/services/briefing-template.service';
import { BriefingModel } from '../../models/briefing.model';
import { ButtonTypesEnum } from '../../../../Common/Forms/enums/button-types.enum';
import {
    CanvasSaveTemplateOption,
    canvasTemplateContentNodes,
    CanvasTemplateOptionEnum,
    canvasTemplateOptionLocalizationKeyMap,
    canvasBlockTypeToTemplateContentOptionMap,
    canvasBlockLocalizationKeyMap
} from './canvasSaveTemplateOptions/canvas-save-template-option.const';
import { BriefingBlockTypeEnum } from '../../enums/briefing-block-type.enum';
import { BriefingGeneralStylesModel } from '../../models/briefing-general-styles.model';
import { LocalizationService } from '../../../../Core/Localization/services/localization.service';
import { BriefingSendAtModel } from '../../models/briefing-send-at.model';
import { BriefingDeliveryMethodEnum } from '../../enums/briefing-delivery-method.enum';
import { CuratedMentionListDisplayElementModel } from '@Monitor/Briefings/models/curated-mention-list-display-element.model';
import { MentionSearchModel } from '@Monitor/Shared/models/mention-search.model';
import { BriefingListItemModel } from '../../../../Common/MonitorShared/model/briefing-list-item.model';


enum SaveTemplateState {
    NEW,
    UPDATE
}

@Component({
    selector: 'agpr-canvas-save-template-dialog',
    templateUrl: './canvas-save-template-dialog.component.html',
    styleUrls: ['./canvas-save-template-dialog.component.scss'],
    standalone: false
})
export class CanvasSaveTemplateDialogComponent extends AbstractDialogComponent implements OnInit, OnDestroy {

    @Input() briefing: BriefingModel | null = null;

    canvasTemplateListItems: DropdownOptionModel<BriefingListItemModel>[] = [];
    existingTemplateDropdown: DropdownOptionModel | null = null;
    newTemplateName: string = '';
    isDuplicateName: boolean = false;

    saveType: SaveTemplateState = SaveTemplateState.NEW;
    formValid: boolean = false;
    canvasSaveTemplateOption: CanvasSaveTemplateOption = {};

    // tree structured treeNode array containing all possible selections
    templateOptions: TreeNode<string>[] = [];

    // flat structured treeNode array containing individual treeNode selections
    selectedOptions: TreeNode<string>[] = [];

    readonly id = 'canvasSaveTemplateDialog';
    readonly saveTemplateState = SaveTemplateState;
    readonly buttonTypes = ButtonTypesEnum;

    private subscriptions: Subscription[] = [];

    constructor(
        public coreDialogService: CoreDialogService,
        private briefingTemplateService: BriefingTemplateService,
        private localizationService: LocalizationService
    ) {
        super(coreDialogService, 'CanvasSaveTemplate');
    }

    ngOnInit() {
        if (this.briefing) {
            this.selectedOptions = [];
            this.templateOptions = this.buildTemplateTree();
            this.handleOptionChange();
        }

        this.subscriptions.push(
            this.briefingTemplateService.all$.subscribe(templates => {
                this.canvasTemplateListItems = templates
                    .filter(item => item.deliveryMethod === BriefingDeliveryMethodEnum.ONLINE)
                    .map(x => new DropdownOptionModel(x.name, x)) || [];
            })
        );

        this.validate();
    }


    private getLabel(option: CanvasTemplateOptionEnum): string {
        return this.localizationService.transform(
            canvasTemplateOptionLocalizationKeyMap.get(option) ?? ''
        );
    }

    // Builds the full template-options tree in one pass so that per-block children
    // are present before the tree component renders — mutating children after the
    // initial render is not picked up by Angular's change detection.
    private buildTemplateTree(): TreeNode<string>[] {
        const treeNodes: TreeNode<string>[] = [];

        canvasTemplateContentNodes.forEach((staticChildren, option) => {
            let children: TreeNode<string>[];

            if (option === CanvasTemplateOptionEnum.CONTENT_BLOCK) {
                // Populate children from the actual briefing blocks
                children = [];
                (this.briefing?.blocks ?? [])
                    .forEach((block, index) => {
                        const optionKey = canvasBlockTypeToTemplateContentOptionMap.get(
                            block.type.key as BriefingBlockTypeEnum
                        );
                        if (optionKey) {
                            children.push({
                                label: this.localizationService.transform(
                                    canvasBlockLocalizationKeyMap.get(optionKey) ?? ''
                                ),
                                data: optionKey + index
                            });
                        }
                    });
            } else {
                children = staticChildren.map(sub => ({
                    label: this.getLabel(sub),
                    data: sub as string
                }));
            }

            treeNodes.push({ label: this.getLabel(option), data: option, children });
        });

        treeNodes.forEach(node => {
            this.selectedOptions.push(node);
            (node.children ?? []).forEach(child => this.selectedOptions.push(child));
        });

        return treeNodes;
    }

    create() {
        const template = this.buildTemplateModel();
        template.name = this.newTemplateName?.trim();
        delete template.id;

        this.briefingTemplateService.createFromBriefing(template).subscribe(() => {
            this.closeDialog();
        });
    }

    update() {
        const template = this.buildTemplateModel();
        template.name = this.existingTemplateDropdown.value.name;
        template.id = this.existingTemplateDropdown.value.id;

        this.briefingTemplateService.updateFromBriefing(template).subscribe(() => {
            this.closeDialog();
        });
    }

    private buildTemplateModel(): BriefingModel {
        if (!this.briefing) {
            return new BriefingModel();
        }
        const template = new BriefingModel();
        template.deliveryMethod              = this.briefing.deliveryMethod;
        template.canvasPreview               = this.briefing.canvasPreview;
        template.name                        = this.briefing.name;
        template.subject                     = this.briefing.subject;
        template.feeds                       = [];
        template.includeEmptyMentionSections = this.briefing.includeEmptyMentionSections;
        template.enableDuplicateMentions     = this.briefing.enableDuplicateMentions;

        template.generalStyles = this.canvasSaveTemplateOption[CanvasTemplateOptionEnum.STYLES]
            ? this.briefing.generalStyles : new BriefingGeneralStylesModel();

        template.sendAt = this.canvasSaveTemplateOption[CanvasTemplateOptionEnum.SCHEDULING]
            ? this.briefing.sendAt : new BriefingSendAtModel();

        // if (!this.canvasSaveTemplateOption[CanvasTemplateOptionEnum.DIGESTS]) {
        //     template.digests = [];
        //     template.selectedDeliveryOption.digest = false;
        // } else {
        //     template.digests = this.briefing.digests?.slice() ?? [];
        // }

        template.mailingListIds = this.canvasSaveTemplateOption[CanvasTemplateOptionEnum.MAILING_LISTS]
            ? (this.briefing.mailingListIds?.slice() ?? []) : [];
        template.recipientList  = this.canvasSaveTemplateOption[CanvasTemplateOptionEnum.RECIPIENTS]
            ? (this.briefing.recipientList?.slice() ?? []) : [];
        template.fromName       = this.canvasSaveTemplateOption[CanvasTemplateOptionEnum.FROM_NAME]
            ? this.briefing.fromName      : '';
        template.replyTo        = this.canvasSaveTemplateOption[CanvasTemplateOptionEnum.REPLY_TO]
            ? this.briefing.replyTo       : '';
        template.inviteMessage  = this.canvasSaveTemplateOption[CanvasTemplateOptionEnum.INVITE_MESSAGE]
            ? this.briefing.inviteMessage : '';

        template.blocks = (this.briefing.blocks ?? []).filter((block, index) => {
            const optionKey = canvasBlockTypeToTemplateContentOptionMap.get(block.type.key as BriefingBlockTypeEnum);

            if (!optionKey) {
                return !!this.canvasSaveTemplateOption[CanvasTemplateOptionEnum.CONTENT_BLOCK];
            }
            return !!this.canvasSaveTemplateOption[optionKey + index];
        }).map(block => {
            if (block.type.key === BriefingBlockTypeEnum.CURATED_MENTION_LIST) {
                if (!this.canvasSaveTemplateOption[CanvasTemplateOptionEnum.SECTION_RULES]) {
                    (block.displayElement as CuratedMentionListDisplayElementModel).searchCriteria = new MentionSearchModel();
                    (block.displayElement as CuratedMentionListDisplayElementModel).searchCriteriaId = null;
                }
            }
            return block.clone();
        });

        return template;
    }

    validate() {
        switch (this.saveType) {
            case SaveTemplateState.NEW: {
                const trimmed = this.newTemplateName.trim();
                this.isDuplicateName = trimmed.length > 0 && this.canvasTemplateListItems.some(
                    item => item.label?.toLowerCase() === trimmed.toLowerCase()
                );
                this.formValid = trimmed.length > 0;
                break;
            }
            case SaveTemplateState.UPDATE:
                this.isDuplicateName = false;
                this.formValid = !!this.existingTemplateDropdown?.value;
                break;
            default:
                this.isDuplicateName = false;
                this.formValid = false;
        }
    }

    handleOptionChange() {
        Object.keys(this.canvasSaveTemplateOption).forEach(
            key => this.canvasSaveTemplateOption[key] = false
        );
        this.selectedOptions.forEach(item => {
            if (item.data != null) {
                this.canvasSaveTemplateOption[item.data as string] = true;
            }
        });
    }

    cancel() {
        this.closeDialog();
    }

    ngOnDestroy() {
        this.subscriptions.forEach(sub => sub.unsubscribe());
    }
}
